import { useState } from "react";
import { useLocation } from "wouter";
import { Box } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { OrderForm } from "./form/order-form";
import { useCreateOrder } from "@/services/order-service";
import {
  OrderCreateFormValues,
  orderCreateSchema,
  OrderStatus,
} from "@/types/order-type";
import {
  FulfillmentMethod,
  FulfillmentCarrier,
} from "@/types/fulfillment-type";
import MyContainer from "@/components/MyContainer";
import { routes } from "@/routes/route-constants";
import { SaveButton } from "@/components/ui/buttons/save-button";

export default function CreateOrderPage() {
  const [, setLocation] = useLocation();
  const [isCreating, setIsCreating] = useState(false);
  const createOrderMutation = useCreateOrder();

  const form = useForm<OrderCreateFormValues>({
    resolver: zodResolver(orderCreateSchema),
    mode: "onChange",
    defaultValues: {
      customerId: "",
      status: OrderStatus.DRAFT,
      notes: "",
      items: [],
      fulfillment: {
        method: FulfillmentMethod.DELIVERY_CITY,
        carrier: FulfillmentCarrier.VIREAK_BUNTHAM,
        price: "2",
      },
    },
  });

  const handleSubmit = async (data: OrderCreateFormValues) => {
    setIsCreating(true);
    try {
      const response = await createOrderMutation.executeAsync(data);

      if (response.data) {
        const createdId = String(response.data.id);
        setLocation(routes.order.detail(createdId));
      }
    } catch (error) {
      console.error("Failed to create order:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSave = () => {
    form.handleSubmit(handleSubmit)();
  };

  const handleCancel = () => {
    setLocation(routes.order.list());
  };

  return (
    <MyContainer
      title="Create New Order"
      subtitle="Select any line item to create order"

      actions={<SaveButton disabled={isCreating} handleSave={handleSave} />}
      footer={
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <SaveButton disabled={isCreating} handleSave={handleSave} />
        </Box>
      }
    >
      <OrderForm
        form={form}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isCreating}
        mode="create"
      />
    </MyContainer>
  );
}
